import React from "react";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import Modal from "@/components/controls/Modal";
import { Button } from "@/components/ui/button";
import Chip from "@/components/ui/chip";
import Text from "@/components/ui/text";
import { Proposal, ProposalRecipient } from "./types";

interface DialogProps {
  // The proposal awaiting confirmation; the dialog is closed while absent.
  proposal?: Pick<Proposal, "name">;
  onConfirm: () => void;
  onCancel: () => void;
}

export const RegenerateProposalDialog: React.FC<DialogProps> = ({
  proposal,
  onConfirm,
  onCancel
}) => {
  const { t } = useTranslation();
  return (
    <ConfirmationModal
      isOpen={!!proposal}
      setIsOpen={(open) => !open && onCancel()}
      title={t("contracts.proposals.regenerateModal.title")}
      confirmationText={t("contracts.proposals.regenerateModal.message")}
      submitButtonText={t("contracts.proposals.regenerateModal.confirm")}
      isPrimaryButton
      hideDivider
      handleSubmit={onConfirm}
      handleCancel={onCancel}
      data-test="proposal-regenerate-dialog"
    />
  );
};

export const DeleteProposalDialog: React.FC<DialogProps> = ({
  proposal,
  onConfirm,
  onCancel
}) => {
  const { t } = useTranslation();
  return (
    <ConfirmationModal
      isOpen={!!proposal}
      setIsOpen={(open) => !open && onCancel()}
      title={t("contracts.proposals.deleteModal.title")}
      confirmationText={t("contracts.proposals.deleteModal.message", {
        name: proposal?.name ?? ""
      })}
      submitButtonText={t("contracts.proposals.deleteModal.confirm")}
      hideDivider
      handleSubmit={onConfirm}
      handleCancel={onCancel}
      data-test="proposal-delete-dialog"
    />
  );
};

// No PandaDoc seat: one Got it button, since ConfirmationModal always adds Cancel.
export const SendAccessRequiredDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const reason = t("contracts.proposals.accessRequiredModal.reason");
  const contactAdmin = t(
    "contracts.proposals.accessRequiredModal.contactAdmin"
  );
  return (
    <Modal
      open={isOpen}
      setIsOpen={(open) => !open && onClose()}
      // Screen readers announce the explanation; the body shows it as designed.
      description={`${reason} ${contactAdmin}`}
      descriptionClassname="sr-only"
      // Sized like ConfirmationModal's, so the X matches the dialogs beside it.
      contentClassname="w-[35rem] [&_[data-test='close-modal']]:w-8 [&_[data-test='close-modal']]:h-8 [&_[data-test='close-modal']]:scale-100"
      data-test="proposal-access-required-dialog"
      // `hideDivider` drops only the body's border; the header carries its own.
      headerClassname="border-b-0"
      title={
        // Every lgSubtitle* variant carries `capitalize`.
        <Text as="div" variant="lgSubtitleH1" className="normal-case">
          {t("contracts.proposals.accessRequiredModal.title")}
        </Text>
      }
      content={
        <div className="p-4">
          <Text as="div" variant="body">
            {reason}
            <br />
            {contactAdmin}
          </Text>
        </div>
      }
      footer={
        <div className="flex justify-end">
          <Button
            onClick={onClose}
            data-test="proposal-access-required-confirm"
          >
            {t("contracts.proposals.accessRequiredModal.confirm")}
          </Button>
        </div>
      }
      hideDivider
    />
  );
};

interface WithdrawalProps {
  // Open while set: the proposal the action is confirmed for; whether its
  // request is withdrawn is each dialog's to say.
  proposal?: Pick<Proposal, "name">;
  // Everyone the request went to, named so the rep sees who they interrupt.
  recipients: ProposalRecipient[];
  onConfirm: () => void;
  onCancel: () => void;
}

const WithdrawalMessage: React.FC<{
  recipients: ProposalRecipient[];
  outcome: string;
}> = ({ recipients, outcome }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2.5">
      <p>{t("contracts.proposals.withdraw.sentTo")}</p>
      <div
        className="flex flex-wrap gap-1"
        data-test="proposal-withdraw-recipients"
      >
        {recipients.map((recipient) => (
          <Chip
            key={recipient.key}
            size="md"
            filled
            className="px-1.5 shadow-field"
            label={recipient.name}
          />
        ))}
      </div>
      <p>
        {t("contracts.proposals.withdraw.linkStops")}
        <br />
        {outcome}
      </p>
    </div>
  );
};

// One whose request already ended has nothing to withdraw: it just returns to
// Draft, so the confirmation names no one.
export const ReviseProposalDialog: React.FC<
  WithdrawalProps & { withdraws: boolean }
> = ({ proposal, recipients, withdraws, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const outcome = t("contracts.proposals.reviseModal.outcome");
  return (
    <ConfirmationModal
      isOpen={!!proposal}
      setIsOpen={(open) => !open && onCancel()}
      title={t("contracts.proposals.reviseModal.title")}
      confirmationText={
        withdraws ? (
          <WithdrawalMessage recipients={recipients} outcome={outcome} />
        ) : (
          outcome
        )
      }
      confirmationTextVariant="body"
      submitButtonText={t(
        withdraws
          ? "contracts.proposals.reviseModal.confirm"
          : "contracts.proposals.reviseModal.endedConfirm"
      )}
      hideDivider
      handleSubmit={onConfirm}
      handleCancel={onCancel}
      data-test="proposal-revise-dialog"
    />
  );
};

// Withdraw leaves the proposal Voided and cuts no new draft, so it works on a
// closed deal too.
export const WithdrawProposalDialog: React.FC<WithdrawalProps> = ({
  proposal,
  recipients,
  onConfirm,
  onCancel
}) => {
  const { t } = useTranslation();
  return (
    <ConfirmationModal
      isOpen={!!proposal}
      setIsOpen={(open) => !open && onCancel()}
      title={t("contracts.proposals.withdrawModal.title")}
      confirmationText={
        <WithdrawalMessage
          recipients={recipients}
          outcome={t("contracts.proposals.withdrawModal.outcome")}
        />
      }
      confirmationTextVariant="body"
      submitButtonText={t("contracts.proposals.withdrawModal.confirm")}
      hideDivider
      handleSubmit={onConfirm}
      handleCancel={onCancel}
      data-test="proposal-withdraw-dialog"
    />
  );
};

// Regenerating a proposal that is still out for signature withdraws it first.
export const WithdrawAndRegenerateDialog: React.FC<WithdrawalProps> = ({
  proposal,
  recipients,
  onConfirm,
  onCancel
}) => {
  const { t } = useTranslation();
  return (
    <ConfirmationModal
      isOpen={!!proposal}
      setIsOpen={(open) => !open && onCancel()}
      title={t("contracts.proposals.regenerateModal.title")}
      confirmationText={
        <WithdrawalMessage
          recipients={recipients}
          outcome={t("contracts.proposals.withdrawRegenerateModal.outcome")}
        />
      }
      confirmationTextVariant="body"
      submitButtonText={t(
        "contracts.proposals.withdrawRegenerateModal.confirm"
      )}
      hideDivider
      handleSubmit={onConfirm}
      handleCancel={onCancel}
      data-test="proposal-withdraw-regenerate-dialog"
    />
  );
};
